import dayjs from "dayjs";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";

// Shared empty stats shape for linguist rows in Pre-Assign / Outreach.
// lastAssignmentDate is the most recent assignment date for the linguist
// (scoped to the selected client), derived from existing service data.
export const EMPTY_LINGUIST_CLIENT_STATS = {
  completedAssignments: 0,
  lastAssignmentDate: null,
};

// Format the difference between today and the last assignment date as a
// compact label using at most 2 units:
//   - only days (< 30 days):            "Xd"            e.g. "5d"
//   - 12 months or less:                "Xm Yd"         e.g. "2m 5d"
//   - more than 12 months:              "Xy Ym"         e.g. "1y 3m"
// Zero secondary units are omitted ("12m", "2y"). Returns "—" when no date.
export const formatLastAssignmentAgo = (dateValue) => {
  if (!dateValue) return "—";
  const date = dayjs(dateValue);
  if (!date.isValid()) return "—";

  const today = dayjs().startOf("day");
  const start = date.startOf("day");

  const diffDays = today.diff(start, "day");
  if (diffDays <= 0) return "0d";

  // Only days
  if (diffDays < 30) return `${diffDays}d`;

  const diffMonths = today.diff(start, "month");
  const diffYears = today.diff(start, "year");

  // More than 12 months → years + remaining months
  if (diffYears >= 1 && diffMonths > 12) {
    const remainingMonths = today.diff(start.add(diffYears, "year"), "month");
    return remainingMonths > 0
      ? `${diffYears}y ${remainingMonths}m`
      : `${diffYears}y`;
  }

  // 12 months or less → months + remaining days
  const remainingDays = today.diff(start.add(diffMonths, "month"), "day");
  return remainingDays > 0 ? `${diffMonths}m ${remainingDays}d` : `${diffMonths}m`;
};

// Build a small informational tooltip for a linguist row in the outreach
// table, reusing the data already present on the linguist object (from
// /linguists/brief) plus the distance/stats that the Pre-Assign screen
// already fetches. Rows are [label, value, { clickable }] where clickable
// rows render an action (e.g. Address can open Maps or be copied).
export const buildLinguistTooltipContent = (
  ling,
  stats = EMPTY_LINGUIST_CLIENT_STATS,
  distanceMiles = null,
) => {
  const rows = [];

  rows.push(["Name", ling?.Name || "—"]);

  const status = String(ling?.Status || "").trim();
  if (status) rows.push(["Status", status]);

  const phones = Array.isArray(ling?.PhoneNumbers)
    ? ling.PhoneNumbers.filter((p) => String(p?.Number || "").trim())
    : [];
  const phone = String(ling?.PhoneNumber || "").trim();
  if (phones.length > 0) {
    rows.push([
      "Phone",
      phones
        .map((p) => {
          const countryCode = String(p?.CountryCode || "").trim();
          const number = String(p?.Number || "").trim();
          const withPrefix =
            countryCode && countryCode !== "1"
              ? `+${countryCode}${number}`
              : number;
          const formatted = normalizePhoneNumber(withPrefix);
          return `${formatted}${p?.Ext ? ` x${p.Ext}` : ""}`;
        })
        .join(", "),
    ]);
  } else if (phone) {
    rows.push(["Phone", normalizePhoneNumber(phone)]);
  }

  const langs = Array.isArray(ling?.LinguistLanguages)
    ? ling.LinguistLanguages.filter(
        (ll) => ll?.Language?.Name || ll?.Language?.name,
      )
    : [];
  if (langs.length > 0) {
    rows.push([
      "Languages",
      langs
        .map((ll) => {
          const name = ll?.Language?.Name || ll?.Language?.name || "?";
          const rate =
            ll?.Rate !== null && ll?.Rate !== undefined
              ? ` $${Number(ll.Rate).toFixed(2)}`
              : "";
          const mode = ll?.Mode ? ` (${ll.Mode})` : "";
          return `${name}${rate}${mode}`;
        })
        .join(", "),
    ]);
  }

  if (ling?.MinimumHours) rows.push(["Min Hours", ling.MinimumHours]);

  const timezone = String(ling?.Timezone || "").trim();
  if (timezone) rows.push(["Timezone", timezone]);

  const address = ling?.AddressRecord?.AddressLine1
    ? [
        ling.AddressRecord.AddressLine1,
        ling.AddressRecord.AddressLine2,
        ling.AddressRecord.AddressLine3,
      ]
        .filter(Boolean)
        .join(", ")
    : String(ling?.Address || "").trim();
  if (address) rows.push(["Address", address, { clickable: true }]);

  if (distanceMiles != null) {
    rows.push(["Distance", `${distanceMiles} mi`]);
  }

  if (stats && Number.isFinite(Number(stats.completedAssignments))) {
    rows.push(["Completed", String(stats.completedAssignments)]);
  }

  if (stats?.lastAssignmentDate) {
    rows.push([
      "Last Assignment",
      `${dayjs(stats.lastAssignmentDate).format("MMM DD, YYYY")} (${formatLastAssignmentAgo(stats.lastAssignmentDate)})`,
    ]);
  }

  return rows;
};